/**
 * AIPipelineMonitor — Dashboard qualité/vitesse de tous les modèles IA.
 *
 * Affiché uniquement si REACT_APP_OCR_DEBUG=true.
 * À retirer avant mise en production (composant temporaire de validation).
 *
 * Props :
 *   ocrComparison  — objet `ocr_comparison` retourné par l'API upload
 *   extractionMeta — { model_used, duration_ms, confidence, input_chars, output_chars, is_mock }
 *   glinerMeta     — { entities_found, duration_ms, enabled } (optionnel)
 */

import { useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';
import { ChevronDown, ChevronRight, FlaskConical, Zap, Shield, Trophy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';

// ── Helpers ────────────────────────────────────────────────────────────────

const scoreColor = (score) => {
  if (score >= 60) return '#16a34a';  // vert
  if (score >= 30) return '#d97706';  // orange
  return '#dc2626';                   // rouge
};

const scoreBg = (score) => {
  if (score >= 60) return 'bg-green-50 border-green-200';
  if (score >= 30) return 'bg-orange-50 border-orange-200';
  return 'bg-red-50 border-red-200';
};

const fmt = (ms) => ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
const pct = (r) => `${(r * 100).toFixed(2)} %`;

// ── Sous-composants ────────────────────────────────────────────────────────

function DebugBadge() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold
                     bg-red-600 text-white tracking-wide">
      DEBUG
    </span>
  );
}

function MetricPill({ label, value, warn }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs
                      border font-medium
                      ${warn ? 'bg-red-50 border-red-300 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
      {warn && '⚠️'} {label}: <b>{value}</b>
    </span>
  );
}

function ScoreGauge({ score, label }) {
  const color = scoreColor(score);
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-black border-4"
        style={{ borderColor: color, color }}
      >
        {Math.round(score)}
      </div>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
  );
}

// ── OCR Section ────────────────────────────────────────────────────────────

function OCRComparison({ data }) {
  const [sampleTab, setSampleTab] = useState('gemma4');
  if (!data) return null;

  const { gemma4, cf_worker, winner, winner_reason } = data;

  // Données pour le RadarChart — 5 axes normalisés sur 100
  const radarData = [
    {
      metric: 'Volume',
      gemma4: gemma4?.quality?.length_score != null
        ? Math.round((gemma4.quality.length_score / 20) * 100) : 0,
      cf_worker: cf_worker?.quality?.length_score != null
        ? Math.round((cf_worker.quality.length_score / 20) * 100) : 0,
    },
    {
      metric: 'Dosages',
      gemma4: gemma4?.quality?.dosage_score != null
        ? Math.round((gemma4.quality.dosage_score / 30) * 100) : 0,
      cf_worker: cf_worker?.quality?.dosage_score != null
        ? Math.round((cf_worker.quality.dosage_score / 30) * 100) : 0,
    },
    {
      metric: 'Posologies',
      gemma4: gemma4?.quality?.posologie_score != null
        ? Math.round((gemma4.quality.posologie_score / 20) * 100) : 0,
      cf_worker: cf_worker?.quality?.posologie_score != null
        ? Math.round((cf_worker.quality.posologie_score / 20) * 100) : 0,
    },
    {
      metric: 'Sans artéfacts',
      gemma4: gemma4?.quality?.artifact_score != null
        ? Math.round((gemma4.quality.artifact_score / 20) * 100) : 0,
      cf_worker: cf_worker?.quality?.artifact_score != null
        ? Math.round((cf_worker.quality.artifact_score / 20) * 100) : 0,
    },
    {
      metric: 'Français',
      gemma4: gemma4?.quality?.fr_score != null
        ? Math.round((gemma4.quality.fr_score / 10) * 100) : 0,
      cf_worker: cf_worker?.quality?.fr_score != null
        ? Math.round((cf_worker.quality.fr_score / 10) * 100) : 0,
    },
  ];

  // BarChart latence
  const latencyData = [
    { name: 'Gemma 4 31B', ms: gemma4?.time_ms || 0, fill: '#6366f1' },
    { name: 'CF Worker',   ms: cf_worker?.time_ms || 0, fill: '#f59e0b' },
  ];

  const engines = [
    { key: 'gemma4',    label: 'Gemma 4 31B', provider: 'Google AI Studio', data: gemma4,    color: '#6366f1' },
    { key: 'cf_worker', label: 'CF Worker',   provider: 'Cloudflare',        data: cf_worker, color: '#f59e0b' },
  ];

  return (
    <div>
      {/* Winner banner */}
      <div className="flex items-center gap-2 mb-4 p-2 bg-green-50 border border-green-200 rounded-lg">
        <Trophy size={16} className="text-green-600" />
        <span className="text-sm font-semibold text-green-800">
          Winner : {winner === 'gemma4' ? 'Gemma 4 31B (Google)' : 'CF Worker (Cloudflare)'}
        </span>
        <span className="text-xs text-green-600">— {winner_reason}</span>
      </div>

      {/* Score cards + RadarChart */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        {/* Col gauche : scores */}
        <div className="col-span-1 flex flex-col gap-3">
          {engines.map(({ key, label, provider, data: d }) => (
            <div
              key={key}
              className={`rounded-xl border p-3 ${winner === key ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-xs font-bold text-gray-800">{label}</p>
                  <p className="text-xs text-gray-400">{provider}</p>
                </div>
                {winner === key && (
                  <span className="text-lg">✅</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <ScoreGauge score={d?.quality?.score ?? 0} label="score" />
                <div className="flex flex-col gap-1 text-xs text-gray-600">
                  <MetricPill label="dosages" value={d?.quality?.dosages ?? 0} />
                  <MetricPill label="posol." value={d?.quality?.posologies ?? 0} />
                  <MetricPill
                    label="artéfacts"
                    value={pct(d?.quality?.artifact_ratio ?? 0)}
                    warn={(d?.quality?.artifact_ratio ?? 0) > 0.01}
                  />
                  <span className="text-gray-400">
                    {(d?.chars ?? 0).toLocaleString('fr-FR')} chars · {fmt(d?.time_ms ?? 0)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Col centre : RadarChart */}
        <div className="col-span-1 flex flex-col items-center justify-center">
          <p className="text-xs text-gray-500 mb-1">Qualité par dimension</p>
          <ResponsiveContainer width="100%" height={200}>
            <RadarChart data={radarData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
              <PolarGrid />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
              <Radar name="Gemma 4 31B" dataKey="gemma4"
                     stroke="#6366f1" fill="#6366f1" fillOpacity={0.25} strokeWidth={2} />
              <Radar name="CF Worker" dataKey="cf_worker"
                     stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} strokeWidth={2} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 10 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Col droite : BarChart latence */}
        <div className="col-span-1 flex flex-col items-center justify-center">
          <p className="text-xs text-gray-500 mb-1">Latence (ms)</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={latencyData} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}s`} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="ms" radius={[4, 4, 0, 0]}>
                {latencyData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sample text */}
      <Tabs value={sampleTab} onValueChange={setSampleTab}>
        <TabsList className="h-7">
          <TabsTrigger value="gemma4" className="text-xs">Gemma 4 31B</TabsTrigger>
          <TabsTrigger value="cf_worker" className="text-xs">CF Worker</TabsTrigger>
        </TabsList>
        <TabsContent value="gemma4">
          <pre className="text-xs bg-white border border-gray-200 rounded p-2 overflow-y-auto"
               style={{ maxHeight: 160, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {gemma4?.sample || '(vide)'}
          </pre>
        </TabsContent>
        <TabsContent value="cf_worker">
          <pre className="text-xs bg-white border border-gray-200 rounded p-2 overflow-y-auto"
               style={{ maxHeight: 160, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
            {cf_worker?.sample || '(vide)'}
          </pre>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Extraction Section ─────────────────────────────────────────────────────

function ExtractionMetrics({ data }) {
  if (!data) return (
    <p className="text-xs text-gray-400 italic">Pas de métriques extraction (PDF non analysé).</p>
  );

  const { model_used, duration_ms, confidence, input_chars, output_chars, is_mock } = data;
  const confColor = confidence >= 0.85 ? 'text-green-700' : confidence >= 0.75 ? 'text-orange-600' : 'text-red-600';

  return (
    <div className="flex flex-wrap gap-3 items-center">
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Modèle</span>
        <span className="text-sm font-bold text-gray-800">
          {model_used} {is_mock && <span className="text-orange-500">(mock)</span>}
        </span>
      </div>
      <div className="w-px h-8 bg-gray-200" />
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Durée</span>
        <span className="text-sm font-bold text-gray-800">{fmt(duration_ms || 0)}</span>
      </div>
      <div className="w-px h-8 bg-gray-200" />
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Confiance</span>
        <span className={`text-sm font-bold ${confColor}`}>
          {confidence != null ? `${Math.round(confidence * 100)} %` : '—'}
        </span>
      </div>
      <div className="w-px h-8 bg-gray-200" />
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Prompt / Réponse</span>
        <span className="text-sm text-gray-700">
          {(input_chars || 0).toLocaleString('fr-FR')} →{' '}
          {(output_chars || 0).toLocaleString('fr-FR')} chars
        </span>
      </div>
    </div>
  );
}

// ── GLiNER Section ─────────────────────────────────────────────────────────

function GlinerMetrics({ data }) {
  if (!data) return (
    <p className="text-xs text-gray-400 italic">GLiNER non actif (GLINER_PII_API_KEY absente).</p>
  );

  const { entities_found, duration_ms, enabled } = data;

  if (!enabled) return (
    <p className="text-xs text-orange-500">GLiNER désactivé (GLINER_ANONYMIZE_ENABLED=false).</p>
  );

  return (
    <div className="flex flex-wrap gap-3 items-center">
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Modèle</span>
        <span className="text-sm font-bold text-gray-800">nvidia/gliner-pii</span>
      </div>
      <div className="w-px h-8 bg-gray-200" />
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Durée</span>
        <span className="text-sm font-bold text-gray-800">{fmt(duration_ms || 0)}</span>
      </div>
      <div className="w-px h-8 bg-gray-200" />
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Entités PII détectées</span>
        <span className={`text-sm font-bold ${entities_found > 0 ? 'text-blue-700' : 'text-gray-500'}`}>
          {entities_found ?? 0}
        </span>
      </div>
      <div className="w-px h-8 bg-gray-200" />
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Provider</span>
        <span className="text-sm text-gray-700">NVIDIA AI</span>
      </div>
    </div>
  );
}

// ── Composant principal ────────────────────────────────────────────────────

export default function AIPipelineMonitor({ ocrComparison, extractionMeta, glinerMeta }) {
  const [open, setOpen] = useState(false);

  // Guard — n'afficher qu'en mode debug
  if (process.env.REACT_APP_OCR_DEBUG !== 'true') return null;

  const hasOCR = !!ocrComparison;

  return (
    <div
      className="my-4 rounded-xl border-2"
      style={{ background: '#FFFBEB', borderColor: '#FDE68A' }}
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        {/* Header */}
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between px-4 py-3 text-left
                             hover:bg-yellow-50 transition-colors rounded-xl">
            <div className="flex items-center gap-2">
              <FlaskConical size={16} className="text-yellow-600" />
              <span className="text-sm font-bold text-yellow-900">AI Pipeline Monitor</span>
              <DebugBadge />
            </div>
            <div className="flex items-center gap-2">
              {hasOCR && (
                <Badge variant="outline" className="text-xs border-yellow-400 text-yellow-800">
                  OCR {ocrComparison.winner === 'gemma4' ? 'Gemma4' : 'CF'} ·{' '}
                  score {ocrComparison[ocrComparison.winner]?.quality?.score ?? '—'}
                </Badge>
              )}
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4">
            <Tabs defaultValue={hasOCR ? 'ocr' : 'extraction'}>
              <TabsList className="mb-4">
                <TabsTrigger value="ocr" className="gap-1.5">
                  <Zap size={13} /> OCR
                </TabsTrigger>
                <TabsTrigger value="extraction" className="gap-1.5">
                  <FlaskConical size={13} /> Extraction
                </TabsTrigger>
                <TabsTrigger value="anonymization" className="gap-1.5">
                  <Shield size={13} /> Anonymisation
                </TabsTrigger>
              </TabsList>

              <TabsContent value="ocr">
                {hasOCR
                  ? <OCRComparison data={ocrComparison} />
                  : <p className="text-xs text-gray-400 italic">
                      Pas de données OCR (PDF avec couche texte native).
                    </p>
                }
              </TabsContent>

              <TabsContent value="extraction">
                <ExtractionMetrics data={extractionMeta} />
              </TabsContent>

              <TabsContent value="anonymization">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Couche 1 — Regex</p>
                    <p className="text-xs text-gray-500">
                      NIR · Téléphone · Email · Adresses · Noms + titres civils
                      — déterministe, sans API.
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Couche 2 — GLiNER-PII</p>
                    <GlinerMetrics data={glinerMeta} />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <p className="mt-3 text-xs text-yellow-700 italic text-center">
              Ce panneau est temporaire — sera retiré avant mise en production.
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
